import { isEmpty } from 'lodash'
import React, { useCallback, useEffect } from 'react'
import { usePaginatedSearch } from 'scenes/Dashboard/components-next/PaginatedSearch'
import { makeStyles } from 'tss-react/mui'
import { PaginationOptions } from 'utils-next/types'

import { CircularProgress, Divider, Theme, Typography } from '@mui/material'

import {
  getApiSearchEngineHandlers,
  SearchEngineBar,
  SearchEngineCompanyRow,
  SearchEngineSection,
  SubmitData,
} from './components'
import { getEmptySearchQueryData } from './components/SearchEngineBar/SearchEngineBar.data'
import { SearchEngineCompany, SearchEngineQueryData } from './SearchEngine.types'

type SearchEngineContentProps = {
  initialFilters?: SearchEngineQueryData
  isFixedSearch?: boolean
}

export function SearchEngineContent({ initialFilters, isFixedSearch = false }: SearchEngineContentProps) {
  const { classes } = useStyles()
  const { state, searchActions } = useSearchEngineStore(initialFilters, isFixedSearch)

  const { filters, isPreviousData } = state
  const companies = state.data
  const loading = state.status === 'loading'

  const handleSubmit = useCallback(async ({ submitButton, ...searchEngineQueryData }: SubmitData) => {
    const { apiGetSearchEngineCompaniesCsv } = getApiSearchEngineHandlers()

    if (submitButton === 'search') {
      searchActions.setFilters(searchEngineQueryData)
      searchActions.refetch()
    } else {
      await apiGetSearchEngineCompaniesCsv(searchEngineQueryData)
    }

    return () => {
      // Cleanup
    }
  }, [])

  useEffect(() => {
    if (isPreviousData && !isEmpty(filters)) {
      searchActions.refetch()
    }
  }, [])

  return (
    <section className={classes.content}>
      <SearchEngineBar
        className={classes.section}
        initialValues={filters}
        handleSubmit={handleSubmit}
        // disabled={isFixedSearch}
      />
      {companies || loading ? (
        <>
          <Divider />
          <SearchEngineSection className={classes.section} title="Results">
            {loading || isPreviousData ? (
              <CircularProgress color="secondary" />
            ) : (
              <div>
                {isEmpty(companies) ? (
                  <Typography variant="body2">No companies found :(</Typography>
                ) : (
                  (companies || []).map((company) => (
                    <SearchEngineCompanyRow key={company.crunchbaseId} company={company}></SearchEngineCompanyRow>
                  ))
                )}
              </div>
            )}
          </SearchEngineSection>
        </>
      ) : null}
    </section>
  )
}

const SEARCH_ENGINE_RESULTS_STORE_ID = 'search-engine-companies'

function useSearchEngineStore(initialFilters?: SearchEngineQueryData, isFixedSearch?: boolean) {
  const filters = {
    ...getEmptySearchQueryData(),
    ...initialFilters,
  }

  const { state, actions: paginatedSearchActions } = usePaginatedSearch<SearchEngineCompany, SearchEngineQueryData>(
    SEARCH_ENGINE_RESULTS_STORE_ID,
    searchCompaniesQuery,
    { initialFilters: filters, urlSync: !isFixedSearch },
    { enabled: true } /* Fetch always, change to false if we need to use it as a param. */
  )
  return { state, searchActions: { ...paginatedSearchActions } }
}

export async function searchCompaniesQuery(
  _key: string,
  filters: SearchEngineQueryData,
  paginationOptions: PaginationOptions
) {
  const { apiGetSearchEngineCompanies } = getApiSearchEngineHandlers()
  return apiGetSearchEngineCompanies(filters, paginationOptions)
}

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  content: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  section: {
    padding: spacing(4),
  },
}))
